<script>
import {mapGetters} from "vuex";
import {getPictureUrl, formatDate} from "@/lib";
import PageIndicator from "@/components/events/PageIndicator.vue";
import RichText from "@/components/ui/RichText.vue";

export default {
  name: 'UpcomingEventsSlider',
  components: {RichText, PageIndicator},
  data: () => ({
    containerWidth: 0,
    columnWidth: 296,
    columnGap: 16,
    firstVisibleEventIndex: 0,
  }),
  methods: {
    getPictureUrl,
    formatDate,
    onResize() {
      this.containerWidth = this.$refs.sliderContainer.clientWidth
    },
    nextPage() {
      const nextStart = this.firstVisibleEventIndex + this.columnCount;
      if(this.currentPage < this.pageCount) {
        this.firstVisibleEventIndex = nextStart;
      }
    },
    prevPage() {
      const prevStart = this.firstVisibleEventIndex - this.columnCount;
      if (this.currentPage > 1) {
        this.firstVisibleEventIndex = Math.max(0, prevStart);
      }
    },
    getDateText(event) {
      if(!event.SortKey) return "Termin folgt"
      return formatDate(event.Termin.Datum)
    }
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.containerWidth = this.$refs.sliderContainer.clientWidth
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  computed: {
    ...mapGetters('veranstaltungen', ['upcomingEvents']),
    events() {
      const eventsWithDates = this.upcomingEvents.filter(e => e.sortKey);
      const eventsWithoutDates = this.upcomingEvents.filter(e => !e.sortKey);

      return [...eventsWithDates, ...eventsWithoutDates];
    },
    columnCount() {
      return Math.floor((this.containerWidth + this.columnGap) / this.columnWidth);
    },
    maxContainerWidth() {
      const width = this.columnCount * this.columnWidth + (this.columnCount -1) * 16;
      return { 'max-width': `${width}px`}
    },
    paginatedEvents() {
      if(this.pageCount === 1) return this.events;
      return this.events.slice(this.firstVisibleEventIndex, this.firstVisibleEventIndex + this.columnCount);
    },
    pageCount() {
      const count = Math.ceil(this.events.length / this.columnCount);

      if(isNaN(count)) return 0;
      return count;
    },
    currentPage() {
      return Math.floor(this.firstVisibleEventIndex / this.columnCount) + 1;
    },
    pages() {
      if(!this.pageCount || this.pageCount === 0 || this.events.length === 0 || this.pageCount === Infinity) return [];
      return [...Array(this.pageCount).keys().map(i => i+1)];
    }
  }
}
</script>

<template>
<div class="pt-5" ref="sliderContainer">
  <div class="mx-auto event-slider-container" style="width: 100%;" :style="maxContainerWidth">
    <router-link class="event-link" v-for="event in paginatedEvents" :key="event.id" :to="`/veranstaltungen/${event.id}`">
    <v-card class="upcoming-event-card" max-width="296" height="400" elevation="1" >
      <v-img v-if="event.Bilder && event.Bilder.length" width="296" height="198" :src="getPictureUrl(event.Bilder[0])">
        <div v-if="event.Angemeldet" class="signed-up-badge text-center">Bereits angemeldet</div>
      </v-img>
      <div v-else class="upcoming-event-fallback-image primary">
        <v-icon color="white" size="80">$menuEvents</v-icon>
        <div v-if="event.Angemeldet" class="signed-up-badge text-center">Bereits angemeldet</div>
      </div>
      <v-card-title class="upcoming-event-card-title">
        <div class="title-badges">
          <v-chip color="grey lighten-1" x-small label class="material-typ-badge mr-2">{{ getDateText(event) }}</v-chip>
          <v-chip color="grey lighten-1" x-small label class="material-typ-badge">{{event.Adresse.Ort }}</v-chip>
        </div>
        <div class="upcoming-event-card-title-text">
          {{ event.Titel }}
        </div>
      </v-card-title>
      <v-card-text>
        <rich-text  class="upcoming-event-card-text" :markdown-text="event.Text" show-plain-text />
      </v-card-text>
    </v-card>
    </router-link>
  </div>
  <div style="display: flex; align-items: center; justify-content: center;">
    <v-btn icon @click="prevPage">
      <v-icon>mdi-chevron-left</v-icon>
    </v-btn>
    <div class="pagination-section pa-5">
    <page-indicator :active="currentPage === n" class="mx-1" v-for="n in pages" :key="n" />
    </div>
    <v-btn icon @click="nextPage">
      <v-icon>mdi-chevron-right</v-icon>
    </v-btn>
  </div>
</div>
</template>

<style scoped>
.pagination-section {
  display: flex;
  align-items: center;
}

.event-slider-container {
  display: flex;
}
.event-slider-container > * {
  margin: 0 8px;
}

.event-slider-container > *:first-child {
  margin-left: 0;
}

.event-slider-container > *:last-child {
  margin-right: 0;
}

.upcoming-event-card-title {
  color: #B40055;
  font-size: 16px;
  line-height: 24px;
  font-weight: bold;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.upcoming-event-card-title-text {
  width: 100%;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.upcoming-event-fallback-image {
  position: relative;
  width: 100%;
  height: 198px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.signed-up-badge {
  height: 28px;
  background: #B40055;
  font-size: 12px;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 17px;
}

.upcoming-event-card {
  border-radius: 5px !important;
}

.upcoming-event-card-text {
  width: 100%;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

</style>